import { Injectable, QueryList } from "@angular/core";
import { Animal, Entity, Job, JobMaterial, SampleGroupSourceMaterial, TaskJob } from "@common/types";
import { JobPharmaDetailService } from "../../services/job-pharma-detail.service";
import { DataManagerService } from "@services/data-manager.service";
import { WorkflowService } from "src/app/workflow/services/workflow.service";
import { JobService } from "../../../job.service";
import { TableSort, TableSortConfig } from "@common/models";
import { WorkspaceService } from "src/app/workspaces/workspace.service";
import { JobPharmaCoreService } from "../../services";
import { FeatureFlagService } from "@services/feature-flags.service";
import { BulkUpdateAnimalNameRequest, JobPharmaDataAccessService } from "../../services/job-pharma-data-access.service";
import { VocabularyService } from "src/app/vocabularies/vocabulary.service";
import { DragulaService } from "ng2-dragula";
import { LoggingService } from "@services/logging.service";
import { IFacet } from "@common/facet";
import { defaultString } from "@common/util/default-string";
import { SortColumnConfig, SortConfig } from "../../util/sort-config";
import { PartialJobMaterial, PartialMaterial } from "../../models/partial-material";
import { formatDataAutomationId } from "@common/util/format-data-automation-id";
import { toPartialDTO } from "../../util/to-partial-dto";
import { JobPharmaAnimalNicknameService } from "./job-pharma-animal-nickname.service";
import { AnimalJobMaterialExtended } from "../../models/extended-job-material";
import { NgModel } from "@angular/forms";

class DragEvent {
    name: string;
    el: Element;
    target: Element;
    source: Element;
    sibling: Element;
    item: any;
    sourceModel: any[];
    targetModel: any[];
    sourceIndex: number;
    targetIndex: number;
}

@Injectable()
export class JobPharmaAnimalsIndividualTableService {
    constructor(
        private jobPharmaDetailService: JobPharmaDetailService,
        private dataManager: DataManagerService,
        private workflowService: WorkflowService,
        private jobService: JobService,
        private workspaceService: WorkspaceService,
        private featureFlagService: FeatureFlagService,
        private jobPharmaDataAccessService: JobPharmaDataAccessService,
        private vocabularyService: VocabularyService,
        private dragulaService: DragulaService,
        private jobPharmaCoreService: JobPharmaCoreService,
        private loggingService: LoggingService,
        private nicknameService: JobPharmaAnimalNicknameService
    ) { }
    
    trackedUnselections: Set<number> = new Set();
    loadingMaterialDataPromise?: Promise<void>;
    applyAllSelected = false;
    trueAnimalCount: number;

    allRowsSelected: boolean;
    selectedRows: Map<number, (PartialMaterial & PartialJobMaterial)> = new Map();
    readonly COMPONENT_LOG_TAG = 'job-pharma-animals-individual-table';
    private readonly MAX_ANIMAL_NAME_LENGTH: number = 75;
    private readonly MAX_ANIMAL_NICKNAME_LENGTH = 75;
    private dragId: number = null;
    private dragTimeoutId: ReturnType<typeof setTimeout>;

    async getAnimalJobMaterials(jobKey: number, pageNumber: number, orderBy?: string): Promise<AnimalJobMaterialExtended[]> {
        let animalJobMaterials = await this.jobPharmaCoreService.getAnimalJobMaterials(jobKey, pageNumber, orderBy) as AnimalJobMaterialExtended[];
        if (this.featureFlagService.getIsGLP()) {
            animalJobMaterials = animalJobMaterials.filter((jm: JobMaterial) => {
                return !jm.DateOut;
            });
        }

        this.nicknameService.onLoadJobMaterials(jobKey, animalJobMaterials);

        return animalJobMaterials;
    }

    async getAnimalCount(job: Job): Promise<number> {
        return this.jobPharmaCoreService.getAnimalCount(job);
    }

    loadVocabularies() {
        return Promise.all([
            this.vocabularyService.ensureCVLoaded('cv_GenotypeAssays', false),
            this.vocabularyService.ensureCVLoaded('cv_GenotypeSymbols', false)
        ]);
    }

    async removeAnimalJobMaterial(jobMaterial: AnimalJobMaterialExtended): Promise<void> {
        const tasks = jobMaterial.Material.TaskMaterial.map(item => item.TaskInstance);
        const lockedTasks = this.jobPharmaDetailService.getLockedTasks(tasks);
        if (this.jobPharmaDetailService.hasLockedTasks(lockedTasks)) {
            this.jobPharmaDetailService.notifyMaterialsCannotBeRemovedFromLockedTask();
            return;
        }

        const isGLP = this.featureFlagService.getIsGLP();

        const job = jobMaterial.Job;
        const animal = jobMaterial.Material.Animal as Entity<Animal>;
        const taskInstanceKeys = job.TaskJob.map((taskJob: TaskJob) => taskJob.C_TaskInstance_key);
        const samples = jobMaterial.Material.SampleGroupSourceMaterial
            .find((x: any) => x.SampleGroup && x.SampleGroup.Sample && x.SampleGroup.Sample.length > 0
                && taskInstanceKeys.includes(x.SampleGroup.C_TaskInstance_key));

        if (!isGLP && samples) {
            return this.jobPharmaDetailService.notifyAnimalsHaveSamples([animal]);
        }

        try {
            // Try to remove the Animal from the tasks as well
            this.jobPharmaDetailService.busyStart();
            const result = await this.jobPharmaDetailService.tryRemoveAnimalsFromTasks(job, [animal]);

            if (!result.allRemoved) {
                return this.jobPharmaDetailService.notifyAnimalsHaveSamples(result.withData);
            }

            const sampleGroupSourceMaterialToDelete = jobMaterial.Material.SampleGroupSourceMaterial
                .filter((x: SampleGroupSourceMaterial) => x.SampleGroup && taskInstanceKeys.includes(x.SampleGroup.C_TaskInstance_key));

            if (sampleGroupSourceMaterialToDelete) {
                sampleGroupSourceMaterialToDelete.forEach((element: SampleGroupSourceMaterial) => {
                    this.dataManager.deleteEntity(element);
                });
                this.jobPharmaDetailService.tabRefresh('samples', 'groups');
            }

            const taskInstances = job.TaskJob.map(taskJob => taskJob.TaskInstance);

            if (jobMaterial.Material.CohortMaterial?.length) {
                const tasksToDelete = taskInstances.filter(taskInstance => {
                    return taskInstance.TaskMaterial.map(item => item.C_Material_key).includes(animal.C_Material_key);
                });

                tasksToDelete.forEach(task => {
                    this.workflowService.deleteTask(task);
                });
            }

            // Remove the Animal from the Job
            this.jobService.deleteJobMaterial(jobMaterial);

            this.jobPharmaDetailService.tabRefresh('tasks', 'list');
            this.jobPharmaDetailService.tabRefresh('tasks', 'outline');

            if (isGLP) {
                jobMaterial.DateOut = new Date();
            }
            this.jobPharmaCoreService.removeAnimalJobMaterial(jobMaterial);
            // Remove associated nickname data
            this.nicknameService.onJobMaterialRemove(jobMaterial);

        } finally {
            this.jobPharmaDetailService.busyStop();
        }
    }

    async dragStart(job: Job): Promise<void> {
        if (this.dragTimeoutId) {
            clearTimeout(this.dragTimeoutId);
            this.dragTimeoutId = null;
        }

        this.dragId = this.jobPharmaDetailService.startDragAsync('Animal', async () => {
            if (this.loadingMaterialDataPromise){
                await this.loadingMaterialDataPromise;
            }

            return this.getSelectedRows(job);
        });

        let count = 0;
        if (this.loadingMaterialDataPromise && this.applyAllSelected) {
            count = this.trueAnimalCount - this.trackedUnselections.size;
        } else {
            count = this.getSelectedRows(job).length;
        }

        if (count > 1) {
            jQuery('.ui-draggable-dragging')[0].textContent = `${this.selectedRows.size} Animals Selected`;
        }
    }

    dragStop(): void {
        this.dragTimeoutId = setTimeout(() => {
            this.jobPharmaDetailService.stopDrag(this.dragId);
            this.dragTimeoutId = null;
        }, 500);
    }

    saveSortConfig(tableSort: TableSort, facet: IFacet) {
        // Start from scratch
        const config = new SortConfig();

        if (tableSort.nested) {
            tableSort.properties.forEach((path: any) => {
                const columnConfig = new SortColumnConfig();
                columnConfig.selected = true;
                columnConfig.reverse = tableSort.nestedReverse[path];
                config.columns[path] = columnConfig;
            });
        } else {
            if (tableSort.propertyPath !== '') {
                const columnConfig = new SortColumnConfig();
                columnConfig.selected = true;
                columnConfig.reverse = tableSort.reverse;
                config.columns[tableSort.propertyPath] = columnConfig;
            }
        }

        // Rebuild the BulkDataConfiguration JSON
        facet.BulkDataConfiguration = JSON.stringify(config);

        // Save just the BulkDataConfiguration value in the facet
        this.workspaceService.saveBulkDataConfiguration(facet);
    }

    allSelectedChanged(animalJobMaterials: AnimalJobMaterialExtended[], job: Job): void {
        /**
         * Clear tracked unselections when select all button is clicked.
         * This is responsible for keeping tracking of user unselecting rows
         * while get all animal request is in transit so when data is loaded in,
         * we know to unselect some of them.
         */
        this.trackedUnselections.clear();

        if (this.allRowsSelected) {
            // Select all
            this.applyAllSelected = true;
            
            // Update current page of non-deleted animal job materials for UI purposes
            animalJobMaterials.forEach((jm) => {
                if (!(<Entity<AnimalJobMaterialExtended>>jm).entityAspect?.entityState?.isDeleted()) {
                    jm.isSelected = true;
                }
            });

            if (this.loadingMaterialDataPromise) {
                // Do not make additional calls to get all animal if request is in transit
                return;
            }

            this.loadingMaterialDataPromise = this.handleGetAllAnimalMaterialsOnJob(job, animalJobMaterials);
        } else {
            // Unselect all
            if (this.loadingMaterialDataPromise) {
                // Keeps track of select all action while isLoadingMaterialData is still not resolved
                this.applyAllSelected = false;
            }

            this.clearAnimalSelections(job, animalJobMaterials);
        }
    }

    private async handleGetAllAnimalMaterialsOnJob(job: Job, animalJobMaterials: AnimalJobMaterialExtended[]) {
        try {
            const jobMaterials = await this.jobPharmaCoreService.getAllAnimalJobMaterialsOnJob(job);
            if (!this.applyAllSelected) {
                // Do nothing if user unselects all while request is in transit
                return;
            }

            this.selectedRows.clear();
            // Add materials to selected rows only for rows that were not unselected during get all animals request
            jobMaterials.forEach(jm => !this.trackedUnselections.has(jm.C_JobMaterial_key) && this.selectedRows.set(jm.C_JobMaterial_key, jm));
        } catch(err) {
            this.loggingService.logError("Something went wrong getting all animals on job", err, this.COMPONENT_LOG_TAG, false);
            animalJobMaterials.forEach((jm) => this.selectedRows.set(jm.C_JobMaterial_key, toPartialDTO(jm)));
        } finally {
            this.loadingMaterialDataPromise = null;
            this.trackedUnselections.clear();
            this.updateSelections(animalJobMaterials);
            this.selectionChanged(job, animalJobMaterials);
        }
    }

    async clearAnimalSelections(job: Job, animalJobMaterials: AnimalJobMaterialExtended[], reload?: boolean) {
        this.selectedRows.clear();
        this.updateSelections(animalJobMaterials);
        await this.selectionChanged(job, animalJobMaterials);

        if (reload) {
            this.jobPharmaCoreService.cleanupIgnoredAnimalsOnJob(job);
        }

    }

    updateSelections(animalJobMaterials: AnimalJobMaterialExtended[]): void {
        animalJobMaterials?.forEach((jm) => jm.isSelected = this.selectedRows.has(jm.C_JobMaterial_key));
    }

    handleUIUpdatesIfLoadingMaterialData(animalJobMaterials: AnimalJobMaterialExtended[]) {
        if (this.loadingMaterialDataPromise) {
            animalJobMaterials.forEach(jm => {
                if (this.applyAllSelected && !this.trackedUnselections.has(jm.C_JobMaterial_key)) {
                    jm.isSelected = true;
                }
            });
        }
    }

    isSelectedChanged(job: Job, animalJobMaterials: AnimalJobMaterialExtended[], jobMaterial: AnimalJobMaterialExtended): void {
        // Check if all the rows are selected
        if (jobMaterial.isSelected) {
            this.selectedRows.set(jobMaterial.C_JobMaterial_key, toPartialDTO(jobMaterial));

            if (this.loadingMaterialDataPromise) {
                this.trackedUnselections.delete(jobMaterial.C_JobMaterial_key);
            }
        } else {
            this.selectedRows.delete(jobMaterial.C_JobMaterial_key);

            if (this.loadingMaterialDataPromise) {
                this.trackedUnselections.add(jobMaterial.C_JobMaterial_key);
            }
        }

        this.selectionChanged(job, animalJobMaterials)
    }

    async selectionChanged(job: Job, animalJobMaterials: AnimalJobMaterialExtended[]){
        this.trueAnimalCount = await this.jobPharmaCoreService.getAnimalCountMinusPendingDeletions(job);

        if (this.loadingMaterialDataPromise) {
            await this.loadingMaterialDataPromise;
        }

        const rows = this.getSelectedRows(job);
        if (rows.length === this.trueAnimalCount && (animalJobMaterials.filter((jm: Entity<AnimalJobMaterialExtended>) => !jm.entityAspect.entityState.isDeleted()).length > 0 || this.applyAllSelected)) {
            this.allRowsSelected = true;
        } else {
            this.allRowsSelected = false;
        }

    }

    getSelectedRows(job: Job) {
        const ignoredAnimalsOnJob = this.jobPharmaCoreService.getIgnoredAnimalJobMaterialKeysOnJob(job, true);
        return Array.from(this.selectedRows.values()).filter(jm => !ignoredAnimalsOnJob.has(jm.C_JobMaterial_key));
    }

    reorderOnDrag(event: DragEvent, animalJobMaterials: AnimalJobMaterialExtended[], dragulaBagName: string): AnimalJobMaterialExtended[] {
        const {el, targetModel, targetIndex} = event;
        const draggedKey = parseInt((el as HTMLElement).dataset.key, 10);
        const draggedLookup: {[key: number]: AnimalJobMaterialExtended} = {}
        const dragged: AnimalJobMaterialExtended[] = [];

        if (draggedLookup[targetIndex]) {
            this.dragulaService.find(dragulaBagName).drake.cancel(true);
            return;
        }

        animalJobMaterials.forEach((jm, index) => {
            if (jm.isSelected || jm.C_Material_key === draggedKey){
                draggedLookup[index] = jm;
                dragged.push(jm);
            }
        });

        if (dragged.length > 1) {
            const front = []
            const back = []

            for (let i = 0; i < animalJobMaterials.length; i++) {
                const jm = animalJobMaterials[i]
                if (draggedLookup[i] === jm) {
                    continue
                } 

                if (i <= targetIndex && targetIndex !== 0) {
                    front.push(jm)
                }

                if (i > targetIndex || targetIndex === 0) {
                    back.push(jm)
                }
            }

            animalJobMaterials = [...front, ...dragged, ...back];
            this.dragulaService.find(dragulaBagName).drake.cancel(true);
        } else {
            animalJobMaterials = targetModel;
        }
        return animalJobMaterials
    }
    

    parseSortConfig(facet: any): SortConfig {
        try {
            if (facet.BulkDataConfiguration) {
                return JSON.parse(facet.BulkDataConfiguration);
            }
        } catch (e) {
            console.error('Could not parse BulkDataConfiguration', e);
        }

        return new SortConfig();
    }

    setDefaultTableSort(tableSort: TableSort, facet: IFacet): TableSort {
        if (!tableSort) {
            tableSort = new TableSort();
        }
        const config = this.parseSortConfig(facet);
        const columns = Object.keys(config.columns);
        if (columns.length === 0) {
            return new TableSort();
        }
        tableSort.nested = columns.length > 1;
        tableSort.natural = true;
        if (tableSort.nested) {
            const properties: string[] = [];
            const reverses: {[key: string]: boolean} = {};
            const indexes: {[key: string]: number} = {};
            columns.forEach((key, index) => {
                reverses[key] = config.columns[key].reverse;
                indexes[key] = index + 1;
                properties.push(key);
            });
            tableSort.setSortSetup(properties, reverses, indexes);
        } else {
            const key = columns[0];
            tableSort.setSingleSortSetup(key, config.columns[key].reverse);
        }

        return tableSort;
    }

    checkPageSequence(animalJobMaterials: AnimalJobMaterialExtended[], page: number): boolean {
        if (!animalJobMaterials || !animalJobMaterials.length) {
            return;
        }

        let sequence = ((page - 1) * 50);
        const jobMaterials = animalJobMaterials.filter((jm) => jm?.Material?.Animal);
        const sequenceCheck = jobMaterials.every((jm) => {
            return jm.Sequence === ++sequence
        });
        return sequenceCheck;
    }

    fixPageSequence(animalJobMaterials: AnimalJobMaterialExtended[], page: number): AnimalJobMaterialExtended[] {
        let sequence = (page - 1) * 50;
        const jobMaterials = animalJobMaterials.filter((jm) => jm?.Material?.Animal);
        jobMaterials.forEach(jm => jm.Sequence = ++sequence);
        return jobMaterials
    }

    async updateAnimalSequence(jobKey: number, tableSort: TableSortConfig[]): Promise<void> {
        const data = {JobKey: String(jobKey), TableSorts: tableSort};
        const animalSequenceCheck = await this.jobPharmaDataAccessService.checkAnimalSequence(data);
        if (animalSequenceCheck){
            await this.jobPharmaDataAccessService.updateAnimalSequence(data);
        }
        if (animalSequenceCheck) {
            this.loggingService.logWarning(
                `Updated animal sequence. Animal sequence determines the animal order in the Workflow facet's Bulk Data Entry.`,
                null,
                this.COMPONENT_LOG_TAG,
                true);
            this.loggingService.logFacetSaveSuccess(this.COMPONENT_LOG_TAG, true)?.attr("data-automation-id", formatDataAutomationId("changes-saved", "", "-text"));
        }
    }

    async bulkNameUpdated(bulkNamePrefix: string, bulkNameSuffix: string, bulkNameCounter: number, jobKey: number): Promise<void> {
        try {
            const prefix = defaultString(bulkNamePrefix, '');
            const suffix = defaultString(bulkNameSuffix, '');

            // Validate max length of generated string
            const animalCount = await this.jobPharmaDataAccessService.getAnimalCount(jobKey);
            const isNameStringValid = this.validateGeneratedStringMaxLength('name',
                prefix, suffix, bulkNameCounter, animalCount, this.MAX_ANIMAL_NAME_LENGTH);

            if (!isNameStringValid) {
                return;
            }

            const data: BulkUpdateAnimalNameRequest = {
                JobKey: jobKey,
                Prefix: prefix,
                Suffix: suffix,
                Counter: bulkNameCounter
            }

            await this.jobPharmaDataAccessService.bulkUpdateAnimalName(data);
            this.loggingService.logWarning(
                `Animal Names Updated.`,
                null,
                this.COMPONENT_LOG_TAG,
                true);
        } catch (err) {
            this.loggingService.logError("Error bulk updating animal name.", err, this.COMPONENT_LOG_TAG, true)
        }
    }

    /**
     * Generates nickname on study
     */
    async generateNicknameOnStudy(job: Job, bulkNicknamePrefix: string, bulkNicknameSuffix: string, bulkNicknameCounter: number): Promise<void> {
        try {
            const prefix = defaultString(bulkNicknamePrefix, '');
            const suffix = defaultString(bulkNicknameSuffix, '');
            const selectedJobMaterialKeys = this.getSelectedRows(job)?.map(x => x.C_JobMaterial_key);

            // Validate max length of generated string
            const animalCount = selectedJobMaterialKeys?.length
                ? selectedJobMaterialKeys.length
                : await this.jobPharmaDataAccessService.getAnimalCount(job.C_Job_key);
            const isNicknameStringValid = this.validateGeneratedStringMaxLength('nickname',
                prefix,
                suffix,
                bulkNicknameCounter,
                animalCount,
                this.MAX_ANIMAL_NICKNAME_LENGTH);

            if (!isNicknameStringValid) {
                return;
            }

            const message = await this.nicknameService.generateNicknameOnJob(
                selectedJobMaterialKeys,
                prefix,
                suffix,
                bulkNicknameCounter);

            if (message) {
                this.loggingService.logWarning(
                    message,
                    null,
                    this.COMPONENT_LOG_TAG,
                    true);
            }

        } catch (err) {
            this.loggingService.logError("Error bulk updating animal nickname on study.", err, this.COMPONENT_LOG_TAG, true)
        }
    }

    /**
     * Updates nickname data with job material when job material is changed
     */
    onNicknameChange(updatedJobMaterial: AnimalJobMaterialExtended): void {
        this.nicknameService.onNicknameChange(updatedJobMaterial);
    }

    /**
     * Returns the value indicating if at least one nickniame of loaded job materials is changed
     */
    isNicknameChanged(): boolean {
        return this.nicknameService.isNicknameChanged();
    }

    /**
     * Validates nickname controls and sets errors if needed
     */
    validateNicknameControls(nicknameControls: QueryList<NgModel>) {
        this.nicknameService.validateNicknameControls(nicknameControls);
    }

    /**
     * Calculates max length of generated string, compares it to maxLength, shows toastr message if calculated length more than @param maxLength
     * @returns true if max length of generated string less then @param maxLength
     */
    private validateGeneratedStringMaxLength(entityPropertyName: string, bulkPrefix: string, bulkSuffix: string, bulkCounter: number, animalCount: number, maxLength: number): boolean {
        if (animalCount === 0) {
            return true;
        } else if (animalCount < 0) {
            throw 'animalsCount can\'t be less than zero';
        }

        const getNumberStringLength = (num: number): number =>
            num < 0 ? 1 + Math.abs(num).toString().length : num.toString().length;

        const prefixLength = bulkPrefix.length;
        const suffixLength = bulkSuffix.length;
        const maxNumberLength =
            animalCount > 0
                ? Math.max(
                    getNumberStringLength(bulkCounter),
                    getNumberStringLength(bulkCounter + animalCount - 1)
                )
                : 0;

        const totalLength = prefixLength + suffixLength + maxNumberLength;
        const isValid = totalLength <= maxLength;

        if (!isValid) {
            this.loggingService.logError(
                `Unable to update animal ${entityPropertyName}${(animalCount > 1 ? 's' : '')} because new ${entityPropertyName} length exceeds ${maxLength} characters.`,
                null,
                this.COMPONENT_LOG_TAG,
                true);
        }

        return isValid;
    }
}
